/**
 * iurop-scratch-popup.js — Rasca y gana (Gudslip, reutilizable iUROP)
 *
 * - Premio aleatorio PONDERADO (pesos de los ajustes, normalizados aquí).
 * - Rascado real por canvas: la textura se borra con el puntero
 *   (destination-out) y al superar el umbral de % rascado se revela el premio.
 * - Alta en Klaviyo por la CLIENT API (pública, pensada para navegador):
 *   1) /client/subscriptions → perfil con consentimiento email SUBSCRIBED
 *   2) /client/events → métrica "Scratch Win" {discount_code, discount_pct}
 *   Tras aceptar ambas peticiones se muestra el código en el propio popup.
 * - Persistencia: iurop:scratch-done (jugó+email → nunca más) ·
 *   iurop:scratch-snooze (cerró → reaparece a los N días).
 * - En el editor de temas (design mode) se abre al seleccionar la sección.
 */
(function () {
  'use strict';

  var DONE_KEY = 'iurop:scratch-done';
  var SNOOZE_KEY = 'iurop:scratch-snooze';
  var KLAVIYO_REVISION = '2026-01-15';

  function lsGet(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } }
  function lsSet(k, v) { try { window.localStorage.setItem(k, v); } catch (e) { /* bloqueado: el popup funciona igual */ } }

  function init() {
    var root = document.querySelector('[data-scratch-popup]');
    if (!root || root.dataset.scratchReady) return;
    root.dataset.scratchReady = '1';

    var designMode = root.getAttribute('data-design-mode') === 'true';
    var enabled = root.getAttribute('data-enabled') === 'true';
    var delayMs = (parseFloat(root.getAttribute('data-delay')) || 0) * 1000;
    var snoozeDays = parseFloat(root.getAttribute('data-snooze-days')) || 7;
    var threshold = (parseFloat(root.getAttribute('data-scratch-threshold')) || 45) / 100;

    var canvas = root.querySelector('[data-scratch-canvas]');
    var card = root.querySelector('[data-scratch-card]');
    var form = root.querySelector('[data-scratch-form]');
    var success = root.querySelector('[data-scratch-success]');
    var errorEl = root.querySelector('[data-scratch-error]');
    var pctEl = root.querySelector('[data-scratch-pct]');
    var subtitleEl = root.querySelector('[data-scratch-subtitle]');

    // ── premio ponderado ────────────────────────────────────────────────────
    var prizes = [];
    try { prizes = JSON.parse(root.querySelector('[data-scratch-prizes]').textContent) || []; } catch (e) {}
    prizes = prizes.filter(function (p) { return p && p.code && p.weight > 0; });
    if (!prizes.length) return;

    function pickPrize() {
      var total = prizes.reduce(function (s, p) { return s + p.weight; }, 0);
      var r = Math.random() * total;
      for (var i = 0; i < prizes.length; i++) {
        r -= prizes[i].weight;
        if (r <= 0) return prizes[i];
      }
      return prizes[prizes.length - 1];
    }
    var prize = null;

    // ── abrir / cerrar ──────────────────────────────────────────────────────
    var opened = false;
    function openPopup() {
      if (opened) return;
      opened = true;
      prize = pickPrize();
      if (pctEl) pctEl.textContent = prize.pct + '%';
      root.hidden = false;
      document.documentElement.classList.add('gudslip-scratch-open');
      // setTimeout y NO requestAnimationFrame: rAF se congela en pestañas en
      // segundo plano y el popup no llegaba a abrirse al volver a la pestaña.
      window.setTimeout(function () {
        root.classList.add('is-open');
        setupCanvas();
      }, 30);
    }
    function closePopup(playedAndDone) {
      root.classList.remove('is-open');
      document.documentElement.classList.remove('gudslip-scratch-open');
      window.setTimeout(function () { root.hidden = true; }, 350);
      if (!playedAndDone && !designMode) {
        lsSet(SNOOZE_KEY, String(Date.now() + snoozeDays * 864e5));
      }
    }
    root.querySelector('[data-scratch-close]').addEventListener('click', function () { closePopup(false); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && opened && !root.hidden) closePopup(false);
    });

    // ── canvas de rascado ───────────────────────────────────────────────────
    var ctx, revealed = false, strokes = 0;
    function setupCanvas() {
      if (!canvas || canvas.dataset.ready) return;
      canvas.dataset.ready = '1';
      var rect = card.getBoundingClientRect();
      var dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      ctx = canvas.getContext('2d');
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () { ctx.drawImage(img, 0, 0, canvas.width, canvas.height); };
      img.onerror = function () {
        // Sin textura (p. ej. asset caído): capa dorada plana, el juego sigue.
        ctx.fillStyle = '#d8c29a';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      };
      img.src = canvas.getAttribute('data-foil');

      var scratching = false, last = null;
      function pos(e) {
        var r = canvas.getBoundingClientRect();
        return {
          x: ((e.clientX - r.left) / r.width) * canvas.width,
          y: ((e.clientY - r.top) / r.height) * canvas.height
        };
      }
      function scratchTo(p) {
        ctx.globalCompositeOperation = 'destination-out';
        ctx.lineWidth = canvas.width * 0.14;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo((last || p).x, (last || p).y);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
        last = p;
      }
      function clearedRatio() {
        var data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
        var clear = 0, sampled = 0;
        for (var i = 3; i < data.length; i += 32) { // cada 8º píxel
          sampled++;
          if (data[i] === 0) clear++;
        }
        return sampled ? clear / sampled : 0;
      }
      function maybeReveal(force) {
        if (revealed) return;
        // Muestrear píxeles es caro: solo cada pocos trazos o al soltar.
        if (!force && ++strokes % 6 !== 0) return;
        if (clearedRatio() >= threshold) reveal();
      }
      function reveal() {
        if (revealed) return;
        revealed = true;
        card.classList.add('is-revealed');
        if (subtitleEl) subtitleEl.hidden = true;
        if (form) {
          form.hidden = false;
          var em = form.querySelector('[data-scratch-email]');
          if (em && window.matchMedia('(min-width: 750px)').matches) em.focus();
        }
      }
      canvas.addEventListener('pointerdown', function (e) {
        scratching = true; last = null; scratchTo(pos(e));
        canvas.setPointerCapture(e.pointerId);
      });
      canvas.addEventListener('pointermove', function (e) {
        if (!scratching) return;
        e.preventDefault();
        scratchTo(pos(e));
        maybeReveal(false);
      });
      canvas.addEventListener('pointerup', function () { scratching = false; last = null; maybeReveal(true); });
      canvas.addEventListener('pointercancel', function () { scratching = false; last = null; });
    }

    // ── Klaviyo ─────────────────────────────────────────────────────────────
    var companyId = (root.getAttribute('data-klaviyo-key') || '').trim();
    var listId = (root.getAttribute('data-klaviyo-list') || '').trim();

    function profileAttributes(email) {
      // País activo de Shopify, no inferido del idioma ni del dominio del email.
      // Los filtros de Gudslip comparan location.country con estos nombres exactos.
      var iso = (root.getAttribute('data-country-code') || '').trim().toUpperCase();
      var countries = {
        ES: 'Spain', AR: 'Argentina', MX: 'Mexico', CO: 'Colombia', PE: 'Peru',
        BR: 'Brazil', AD: 'Andorra', CL: 'Chile', EC: 'Ecuador', VE: 'Venezuela',
        UY: 'Uruguay', PY: 'Paraguay', BO: 'Bolivia', GT: 'Guatemala', HN: 'Honduras',
        US: 'United States', GB: 'United Kingdom', DE: 'Germany', FR: 'France',
        IT: 'Italy', PT: 'Portugal', CA: 'Canada', AU: 'Australia'
      };
      var country = countries[iso];
      if (!country && /^[A-Z]{2}$/.test(iso)) {
        try { country = new Intl.DisplayNames(['en'], { type: 'region', fallback: 'none' }).of(iso); } catch (e) {}
      }
      // No registrar un alta sin el dato que requieren los flows ni inventar Spain.
      if (!country || country === iso) throw new Error('Profile country missing');
      var language = (root.getAttribute('data-language-code') || '').trim();
      // Klaviyo acepta BCP 47. Conservar el idioma elegido, sin deducirlo del
      // mercado (Spain + en es válido) ni sustituir un dato ausente por "en".
      if (!/^[a-z]{2,3}(?:-[a-zA-Z0-9]{2,8})*$/i.test(language)) throw new Error('Profile locale missing');
      try { language = Intl.getCanonicalLocales(language)[0]; } catch (e) { throw new Error('Profile locale invalid'); }
      var context = {
        scratch_language: language.split('-')[0],
        scratch_locale: language,
        scratch_country_code: iso,
        scratch_country: country,
        scratch_localization_source: 'shopify_localization'
      };
      // La integración Shopify puede actualizar el locale estándar después.
      // Mantener también la captura del popup en propiedades propias y evento.
      // País de compra seleccionado, NO una afirmación de geolocalización por IP.
      return { email: email, location: { country: country }, locale: language, properties: context };
    }

    function klaviyoPost(path, payload) {
      return fetch('https://a.klaviyo.com/client/' + path + '/?company_id=' + encodeURIComponent(companyId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/vnd.api+json', revision: KLAVIYO_REVISION },
        body: JSON.stringify(payload)
      }).then(function (response) {
        if (!response.ok) throw new Error('Klaviyo ' + path + ' ' + response.status);
        return response;
      });
    }

    function subscribe(profile) {
      // Identify no equivale a suscripción. Sin lista, no enviar ni simular éxito.
      if (!listId) return Promise.reject(new Error('Newsletter list missing'));
      var attrs = Object.assign({}, profile);
      attrs.subscriptions = { email: { marketing: { consent: 'SUBSCRIBED' } } };
      var data = {
        type: 'subscription',
        attributes: {
          custom_source: 'Scratch & Win popup',
          profile: {
            data: {
              type: 'profile',
              attributes: attrs
            }
          }
        }
      };
      var payload = { data: data };
      payload.data.relationships = { list: { data: { type: 'list', id: listId } } };
      return klaviyoPost('subscriptions', payload);
    }

    function trackWin(profile) {
      return klaviyoPost('events', {
        data: {
          type: 'event',
          attributes: {
            properties: Object.assign({}, profile.properties, { discount_code: prize.code, discount_pct: prize.pct }),
            metric: { data: { type: 'metric', attributes: { name: 'Scratch Win' } } },
            profile: { data: { type: 'profile', attributes: profile } }
          }
        }
      });
    }

    var submitting = false;
    var submitted = false;
    var acceptedEmail = '';
    var registrationProfile = null;
    var codeInput = root.querySelector('[data-scratch-code]');
    var copyButton = root.querySelector('[data-scratch-copy]');
    var copyStatus = root.querySelector('[data-scratch-copy-status]');

    if (copyButton && codeInput) {
      copyButton.addEventListener('click', function () {
        if (!submitted || !codeInput.value) return;
        function manualCopy() {
          codeInput.focus();
          codeInput.select();
          if (copyStatus) copyStatus.textContent = copyButton.getAttribute('data-copy-help');
        }
        if (!window.navigator.clipboard || !window.navigator.clipboard.writeText) {
          manualCopy();
          return;
        }
        window.navigator.clipboard.writeText(codeInput.value).then(function () {
          if (copyStatus) copyStatus.textContent = copyButton.getAttribute('data-copied-label');
        }).catch(manualCopy);
      });
    }

    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (submitting || submitted) return;
        if (errorEl) errorEl.hidden = true;
        var email = (form.querySelector('[data-scratch-email]').value || '').trim();
        var consent = form.querySelector('[data-scratch-consent]');
        var btn = form.querySelector('[data-scratch-submit]');
        var emailInput = form.querySelector('[data-scratch-email]');
        if (!emailInput.checkValidity()) { emailInput.reportValidity(); return; }
        if (!consent.checked) {
          if (consent && !consent.checked) consent.focus();
          return;
        }
        if (!companyId || !listId || !prize || !codeInput || !success) {
          if (errorEl) {
            errorEl.textContent = form.getAttribute('data-error-text') || 'Error. Try again.';
            errorEl.hidden = false;
          }
          return;
        }
        submitting = true;
        btn.disabled = true;
        // Reintentar un evento fallido no repite un alta ya aceptada en esta sesión.
        Promise.resolve().then(function () {
          // La suscripción y su evento, incluidos reintentos, comparten contexto.
          if (!registrationProfile || registrationProfile.email !== email) registrationProfile = profileAttributes(email);
          return acceptedEmail === email ? undefined : subscribe(registrationProfile);
        })
          .then(function () {
            acceptedEmail = email;
            return trackWin(registrationProfile);
          })
          .then(function () {
            submitted = true;
            lsSet(DONE_KEY, '1');
            codeInput.value = prize.code;
            form.hidden = true;
            success.hidden = false;
            success.focus();
            // Sin cierre automático: el visitante necesita tiempo para copiarlo.
          })
          .catch(function () {
            submitting = false;
            btn.disabled = false;
            if (errorEl) {
              errorEl.textContent = form.getAttribute('data-error-text') || 'Error. Try again.';
              errorEl.hidden = false;
            }
          });
      });
    }

    // ── disparo ─────────────────────────────────────────────────────────────
    if (designMode) {
      // En el editor: abrir al seleccionar la sección, cerrar al deseleccionar.
      document.addEventListener('shopify:section:select', function (e) {
        if (e.target.contains(root)) openPopup();
      });
      document.addEventListener('shopify:section:deselect', function (e) {
        if (e.target.contains(root)) closePopup(false);
      });
      return;
    }
    if (!enabled) return;
    if (lsGet(DONE_KEY) === '1') return;
    var snooze = parseInt(lsGet(SNOOZE_KEY), 10);
    if (snooze && Date.now() < snooze) return;
    // El fondo va lazy en el markup (para no pesar si el visitante no llega a
    // verlo), pero si el popup VA a abrirse se precarga durante el retardo:
    // sin esto se abría con la página visible detrás hasta que llegaba la foto.
    root.querySelectorAll('.gudslip-scratch__bg').forEach(function (img) {
      img.loading = 'eager';
      if (img.dataset && !img.complete && img.src) { var pre = new Image(); pre.src = img.currentSrc || img.src; }
    });
    window.setTimeout(openPopup, delayMs);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
